// The one home for colour in this tree. Everything else — CSS and TS alike —
// reads a token, never a literal, and `node --run tokens` regenerates the
// artefacts below it (src/tokens.css today, the Papier map styles from S1 on).
//
// Values are Papier, verbatim from the prototype's stylesheet
// (docs/design/redesign/prototype/index.html, `[data-theme="jasny"|"ciemny"]`).

export type ThemeName = 'light' | 'dark';
export type TokenName = 'bg' | 'bg2' | 'ink' | 'ink2' | 'hair' | 'brick' | 'ok' | 'tint' | 'paper-map';

/** Papier, exactly as `docs/design/redesign/` records it. */
export const tokens: Record<ThemeName, Record<TokenName, string>> = {
  light: {
    bg: '#F4F1EA',
    bg2: '#ECE8DF',
    ink: '#1C1A17',
    ink2: '#5B564E',
    hair: 'rgba(28,26,23,.16)',
    brick: '#A63B24',
    ok: '#2F6B4F',
    tint: 'rgba(244,241,234,.28)',
    'paper-map': 'repeating-linear-gradient(135deg,#F4F1EA 0 7px,#E6E2D8 7px 8px)',
  },
  dark: {
    bg: '#1B1917',
    bg2: '#26231F',
    ink: '#F1ECE2',
    ink2: '#B9B2A6',
    hair: 'rgba(241,236,226,.18)',
    brick: '#E8734F',
    ok: '#8FCFA9',
    tint: 'rgba(27,25,23,.25)',
    'paper-map': 'repeating-linear-gradient(135deg,#1B1917 0 7px,#2A2723 7px 8px)',
  },
};

/** The order the generated CSS writes them in, so the artefact is stable. */
const TOKEN_NAMES = Object.keys(tokens.light) as TokenName[];

/** One token's value for one theme, for the code that cannot read CSS — the map style. */
export function token(name: TokenName, theme: ThemeName): string {
  return tokens[theme][name];
}

function block(theme: ThemeName, indent: string): string {
  return TOKEN_NAMES.map((name) => `${indent}--${name}: ${tokens[theme][name]};`).join('\n');
}

/** The `:root`, `@media (prefers-color-scheme: dark)` and `:root[data-theme='dark']` blocks. */
export function cssText(): string {
  // `system` sets no attribute, so the media block has to apply to everything
  // except a theme the resident forced to light; `[data-theme='dark']` then
  // overrides a light OS in the other direction. Same shape as src/theme.ts.
  return [
    '/* Generated by `node --run tokens` from src/tokens.ts. Do not edit by hand. */',
    ':root {',
    block('light', '  '),
    '}',
    '@media (prefers-color-scheme: dark) {',
    "  :root:not([data-theme='light']) {",
    block('dark', '    '),
    '  }',
    '}',
    ":root[data-theme='dark'] {",
    block('dark', '  '),
    '}',
    '',
  ].join('\n');
}
